import moment from "moment";

import { IExternalConsultationHistoryGridModel } from "@/Administration/Models/UI/ExternalConsultationHistory/IExternalConsultationHistoryGridModel";
import { XComLogsResponseTableItem } from "@/Administration/Models/UI/XComLogs/XComLogsResponseTableItem";
import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { HyperLinkControl } from "@/Common/Components/Buttons/HyperLinkControl";
import { SpanControl } from "@/Common/Components/SpanControl";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { Locale } from "@/Common/Models/Localization/Locale";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { PatientInfoDialog } from "./PatientInfoDialog";
import { XComLogsHelper } from "./XComLogsHelper";
import { XComLogsSearchSetting } from "./XComLogsSearchSetting";

interface IXComLogsViewProps {
    model: IExternalConsultationHistoryGridModel;
}

class XComLogsViewState {
    public OpenSettingsControls: boolean;
    public Items: Immutable.List<XComLogsResponseTableItem>;
    public OpenPatientPopup: boolean;
    public patientContactId: string;
    public clientTag: string;

    public static GetDefault(): XComLogsViewState {
        const s = new XComLogsViewState();
        s.OpenSettingsControls = false;
        s.OpenPatientPopup = false;
        s.patientContactId = null;
        s.clientTag = null;
        s.Items = [].toImmutable();
        return s;
    }
}

class XComLogsView extends BaseReactComponent<IXComLogsViewProps, XComLogsViewState> {
    private helper: MyleHelper = new MyleHelper();
    private saveOptionKey: string = "XcomLogsGrid";

    constructor(props: IXComLogsViewProps) {
        super(props);
        this.state = XComLogsViewState.GetDefault();
    }

    public componentDidUpdate(prevProps: IXComLogsViewProps): void {
        if (prevProps.model !== this.props.model)
            this.refreshData(this.props);
    }

    public componentDidMount(): void {
        window.setTimeout(() => {
            $(window).trigger("resize");
        }, 50);
        this.refreshData(this.props);
    }

    public render(): JSX.Element {
        let columnsOptions = this.helper.loadTableColumnOptionsFromSettings(this.saveOptionKey, this.getMyleTableColumnsDefaultOptions());
        let tableRenderOptions = this.loadTableRenderOptions(columnsOptions);
        const tableColumns = this.getTableColumns(columnsOptions);

        return (
            <div className="workspace">
                <CommonTableHeaderComponent
                    title={cacheStore.GetString("ExternalConsultationHistoryLabel")}
                    searchText={this.props.model.get("SearchText")}
                    searchPlaceholder={cacheStore.GetString("SearchField")}
                    onSearch={text => sessionStore.dispatch(LocalOperations.AdministrationXComLogs.SearchSettings_SearchTextChanged, { filterText: text })}
                    onRefreshClick={async () => await this.refreshData(this.props)}
                    onSettingsClick={() => this.mutateState(s => s.OpenSettingsControls = !s.OpenSettingsControls)}
                />
                {this.state.OpenPatientPopup && (
                    <PatientInfoDialog
                        onClose={() => this.mutateState(s => s.OpenPatientPopup = false)}
                        patientContactId={this.state.patientContactId}
                        clientTag={this.state.clientTag}
                    />
                )}
                <div id="posref" className="ws-scrollable">
                    {this.state.OpenSettingsControls && (
                        <XComLogsSearchSetting
                            id="externalcombosettings"
                            model={this.props.model}
                            className="filter-component filter-component-responsive"
                            onCancelCallBack={() => this.mutateState(s => s.OpenSettingsControls = false)}
                        />
                    )}

                    <div>
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="adminExternalCom_table"
                            Columns={Immutable.List<MyleTableColumn>(tableColumns)}
                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                            DataSource={this.state.Items}
                            SaveOptionskeyTarget={this.saveOptionKey}
                        />
                    </div>
                    <div className="clearfix" />
                </div>

                <div className="ws-footer table-pager-footer">
                    <div className="row">
                        <div className="col-lg-4 col-md-4 col-sm-4">
                            <SimplePagerControl
                                PageSize={this.props.model.get("PageSize")}
                                CurrentPage={this.props.model.get("PageSelected")}
                                TotalRows={9999}
                                EventToDispatchOnPageChange={LocalOperations.AdministrationXComLogs.OnPageChange}
                            />
                        </div>
                    </div>
                </div>

            </div>
        );
    }

    private getTableColumns(columnOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<XComLogsResponseTableItem>(
            columnOptions,
            column => {
                switch (column.FieldName) {
                    case "Timestamp":
                        return {
                            renderCell: item =>
                                (
                                    <div>
                                        <SpanControl text={item.Model.Timestamp ? moment(item.Model.Timestamp).format("YYYY-MM-DD HH:mm:ss") : ""} />
                                    </div>
                                )
                        };
                    case "EventType":
                        return {
                            renderCell: item =>
                                (
                                    <div>
                                        <SpanControl
                                            text={XComLogsHelper.getEventTypeName(item.Model.EventType)}
                                        />
                                    </div>
                                )
                        };
                    case "FileType":
                        return {
                            renderCell: item =>
                                (
                                    <div>
                                        <SpanControl
                                            text={XComLogsHelper.getFileTypeName(item.Model.FileType)}
                                        />
                                    </div>
                                )
                        };
                    case "FromClientTag":
                        return {
                            renderCell: item =>
                                (
                                    <div><SpanControl text={item.Model.FromClientTag} /> </div>
                                )
                        };
                    case "EmployeeId":
                        return {
                            renderCell: item => {
                                if (!IsNullOrWhiteSpace(item.Model.FromEmployeeFirstName) || !IsNullOrWhiteSpace(item.Model.FromEmployeeLastName)) {
                                    let consultatedBy = `${item.Model.FromEmployeeFirstName} ${item.Model.FromEmployeeLastName}`;
                                    if (!IsNullOrWhiteSpace(item.Model.FromEmployeeLicence))
                                        consultatedBy += ` (${item.Model.FromEmployeeLicence})`;

                                    return (
                                        <div> <SpanControl text={`${consultatedBy}`} /> </div>
                                    );
                                }
                                else {
                                    return (
                                        <div> <SpanControl text={item.Model.EmployeeId} /> </div>
                                    );
                                }
                            }
                        };
                    case "ToClientTag":
                        return {
                            renderCell: item =>
                                (
                                    <div> <SpanControl text={item.Model.ToClientTag} /> </div>
                                )
                        };
                    case "PatientId":
                        return {
                            renderCell: item =>
                                (
                                    <div>
                                        {item.Model.PatientContactId && (
                                            <div>
                                                <HyperLinkControl
                                                    text={item.Model.PatientContactId}
                                                    onChange={() => this.openPatientPopup(item.Model.PatientContactId, item.Model.ToClientTag)}
                                                />
                                            </div>
                                        )}
                                        {item.Model.PatientContactIds && item.Model.PatientContactIds?.map(contactId => (
                                            <div key={`xcomPatientLink_${contactId}`}>
                                                <HyperLinkControl
                                                    text={contactId}
                                                    onChange={() => this.openPatientPopup(contactId, item.Model.ToClientTag)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )
                        };
                    case "IPAddress":
                        return {
                            renderCell: item =>
                                (
                                    <div> <SpanControl text={item.Model.IpAddress} /> </div>
                                )
                        };
                    case "IsAccessWithoutConsent":
                        return {
                            renderCell: item =>
                                (
                                    <div>
                                        <SpanControl
                                            text={item.Model.IsAccessWithoutConsent
                                                ? item.Model.IsAccessWithoutConsent
                                                    ? cacheStore.GetString("NoLabel")
                                                    : cacheStore.GetString("YesLabel")
                                                : "--"
                                            }
                                        />
                                    </div>
                                )
                        };
                    case "Motive":
                        return {
                            renderCell: item =>
                                (
                                    <div>
                                        {sessionStore.locale == Locale.en && (
                                            <SpanControl
                                                text={item.Model.Motive?.["en"]}
                                                title={item.Model.Motive?.["en"]}
                                            />
                                        )}
                                        {sessionStore.locale == Locale.fr && (
                                            <SpanControl
                                                text={item.Model.Motive?.["fr"]}
                                                title={item.Model.Motive?.["fr"]}
                                            />
                                        )}
                                        {sessionStore.locale == Locale.es && (
                                            <SpanControl
                                                text={item.Model.Motive?.["es"]}
                                                title={item.Model.Motive?.["es"]}
                                            />
                                        )}
                                    </div>
                                )
                        };
                    case "MotiveComment":
                        return {
                            renderCell: item =>
                                (
                                    <div>
                                        <SpanControl
                                            text={item.Model.MotiveComment}
                                            title={item.Model.MotiveComment}
                                        />
                                    </div>
                                )
                        };
                    default:
                        break;
                }
            },
            false
        );
    }

    private getMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        return [
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ConsultedAtLabel"),
                FieldName: "Timestamp",
                WidthPercentageDefault: 10
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("EventsLabel"),
                FieldName: "EventType",
                WidthPercentageDefault: 10
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("FileTypeLabel"),
                FieldName: "FileType",
                WidthPercentageDefault: 5
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ConsultedFromLabel"),
                FieldName: "FromClientTag",
                WidthPercentageDefault: 10
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ConsultedByLabel"),
                FieldName: "EmployeeId",
                WidthPercentageDefault: 10
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ToLabel"),
                FieldName: "ToClientTag",
                WidthPercentageDefault: 10
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("PatientIDLabel"),
                FieldName: "PatientId",
                WidthPercentageDefault: 10
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("IPAddressLabel"),
                FieldName: "IPAddress",
                WidthPercentageDefault: 10
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ConsentLabel"),
                FieldName: "IsAccessWithoutConsent",
                WidthPercentageDefault: 10
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("MotiveLabel"),
                FieldName: "Motive",
                WidthPercentageDefault: 5
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("NotesLabel"),
                FieldName: "MotiveComment",
                WidthPercentageDefault: 10
            })
        ];
    }

    private loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        let defaultTablePluginOptions = {
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: false,
            LastColumnNoDrag: false,
            Autoresize: true,
            OnColumnWidthChange: null
        };

        return Immutable.fromJS(defaultTablePluginOptions);
    }

    private async refreshData(currentProps: IXComLogsViewProps): Promise<void> {
        const response = await Client.postAsync<any>(
            ServerOperations.Server.Myle_ExternalCommunication_LoadLogEntries,
            {
                PageSelected: currentProps.model.get("PageSelected"),
                PageSize: currentProps.model.get("PageSize"),
                FromDate: currentProps.model.get("DateFrom"),
                ToDate: currentProps.model.get("DateTo"),
                EventTypes: currentProps.model.get("EventTypes"),
                FileTypes: currentProps.model.get("FileTypes"),
                EventDirections: currentProps.model.get("EventDirections"),
                Options: currentProps.model.get("Options"),
                SearchText: currentProps.model.get("SearchText")
            }
        );

        if (!response.isSuccess)
            return;

        this.mutateState(s => {
            s.Items = response.data?.LogEntries
                ?.map(p => new XComLogsResponseTableItem(p))
                .toImmutable()
                ?? [].toImmutable();
        });
    }

    private openPatientPopup(patientContactId: string, clientTag: string): void {
        this.mutateState(s => {
            s.OpenPatientPopup = true;
            s.patientContactId = patientContactId;
            s.clientTag = clientTag;
        });
    }
}

export { XComLogsView };
